import type { QueryClient } from "@tanstack/react-query";

import type { SignedPreviewResponse } from "@/types/auth";
import type { FirebaseProviderSummary, RoleSummary } from "@/types/provider";
import type { Role } from "@/types/user";

import { listProviders } from "./provider";
import { requestData } from "@/api/api-config";
import { getSignedUrl, getSignedUrlBase64 } from "@/api/auth/gcs";

export const providerQueryKeys = {
  all: ["providers"] as const,
  // Matches every list; `list()`'s locationId/permission miss the scoped ones.
  lists: () => [...providerQueryKeys.all, "list"] as const,
  // Append `permission` only when set: existing `invalidateQueries` callers pass
  // `list()`, which stops matching the filtered keys if it is always present.
  list: (
    locationId: string | undefined = undefined,
    permission: string | undefined = undefined,
  ) =>
    permission === undefined
      ? ([...providerQueryKeys.lists(), locationId] as const)
      : ([...providerQueryKeys.lists(), locationId, permission] as const),
  summary: (providerId: string) =>
    [...providerQueryKeys.all, "summary", providerId] as const,
  roles: (providerIds: string[]) =>
    [...providerQueryKeys.all, "roles", providerIds] as const,
  profileImageSignedUrl: (profileImage: string) =>
    [
      ...providerQueryKeys.all,
      "profile-image-signed-url",
      profileImage,
    ] as const,
  signatureDataUrl: (signatureImage: string) =>
    [...providerQueryKeys.all, "signature-data-url", signatureImage] as const,
};

/** Rewrite every cached provider list in place. Skips lists not yet fetched. */
export const patchCachedProviderLists = (
  queryClient: QueryClient,
  patch: (provider: FirebaseProviderSummary) => FirebaseProviderSummary,
) =>
  queryClient.setQueriesData<FirebaseProviderSummary[]>(
    { queryKey: providerQueryKeys.lists() },
    (providers) => providers?.map(patch),
  );

/** The subset of `roles` named by `roleIds`, in the shape a cached row holds. */
export const toCachedRoles = (
  roles: RoleSummary[],
  roleIds: string[],
): Role[] =>
  roles
    .filter((role) => roleIds.includes(role.id))
    .map(({ id, name, userType, permissions }) => ({
      id,
      name,
      userType,
      permissions: permissions ?? [],
    }));

export async function getProviderRoles(
  providerIds: string[],
): Promise<Record<string, RoleSummary[]> | null> {
  if (providerIds.length === 0) {
    return null;
  }
  return requestData<Record<string, RoleSummary[]>>(
    `admin/providers/roles`,
    "get",
    {
      searchParams: {
        provider_ids: providerIds,
      },
    },
  );
}

export const providerQueryOptions = {
  list: (locationId?: string, permission?: string) => ({
    queryKey: providerQueryKeys.list(locationId, permission),
    queryFn: () => listProviders(locationId, permission),
    select: (data: FirebaseProviderSummary[] | null) => data ?? [],
  }),
  roles: (providerIds: string[]) => ({
    queryKey: providerQueryKeys.roles(providerIds),
    queryFn: () => getProviderRoles(providerIds),
    select: (data: Record<string, RoleSummary[]> | null) => data ?? {},
  }),
  profileImageSignedUrl: (profileImage: string) => ({
    queryKey: providerQueryKeys.profileImageSignedUrl(profileImage),
    queryFn: () => getSignedUrl(profileImage),
    enabled: profileImage.startsWith("gs://"),
    select: (data: SignedPreviewResponse | null) => data?.signedUrl ?? null,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
  }),
  /**
   * A provider's adopted signature as a base64 PNG data URL.
   *
   * Pulled through the backend rather than fetched from `signatureImageUrl`
   * directly: the bucket isn't CORS-enabled for the app origin, so a browser
   * fetch of the signed URL is blocked. Callers that only need to *display* the
   * signature should keep using `signatureImageUrl` (an <img> src isn't subject
   * to CORS) — this is for callers that need the bytes, e.g. embedding the
   * signature into a client-rendered PDF.
   *
   * Never stale: every signature upload writes a new unique path
   * (`signature-<providerId>-<hex>.png`), so a given path's bytes never change.
   * That holds for a *successful* read only, hence the throw below.
   */
  signatureDataUrl: (signatureImage: string) => ({
    queryKey: providerQueryKeys.signatureDataUrl(signatureImage),
    // Throw rather than yield null on failure. `requestData` reports a failed
    // request as null, which the query would otherwise cache as a successful
    // empty result — and under `staleTime: Infinity` that pins the failure for
    // the life of the entry, so the caller keeps reporting the signature
    // unavailable and reopening the form never retries. An error is not cached
    // as fresh, so a remount does try again.
    queryFn: async () => {
      const response = await getSignedUrlBase64(signatureImage);
      if (!response?.signedUrl) {
        throw new Error("Could not load provider signature");
      }
      return response.signedUrl;
    },
    enabled: signatureImage.startsWith("gs://"),
    staleTime: Infinity,
  }),
};
